import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowUp } from 'lucide-react';
import { useTranslation } from 'react-i18next';

/**
 * BackToTopButton - A floating button that appears when user scrolls down
 * Positioned to not overlap with MobileBottomNav
 */
export const BackToTopButton = () => {
  const { i18n } = useTranslation();
  const language = i18n.language?.startsWith('pt') ? 'pt' : 'en';
  const [isVisible, setIsVisible] = useState(false);

  const texts = {
    pt: { backToTop: 'Voltar ao topo' },
    en: { backToTop: 'Back to top' }
  };

  useEffect(() => {
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          // Show button after scrolling down 400px
          setIsVisible(window.scrollY > 400);
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    
    // Initial check
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.button
          initial={{ opacity: 0, scale: 0.8, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.8, y: 20 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
          onClick={scrollToTop}
          className="fixed z-[900] right-4 bg-[#00C2D1] hover:bg-[#00A8B5] text-white p-3 rounded-full shadow-lg transition-colors focus:outline-none focus:ring-2 focus:ring-[#00C2D1] focus:ring-offset-2"
          style={{
            // Position above MobileBottomNav (which is ~70px tall) on mobile
            bottom: 'calc(env(safe-area-inset-bottom, 0px) + 90px)'
          }}
          aria-label={texts[language].backToTop}
          data-testid="back-to-top-button"
        >
          <ArrowUp className="w-5 h-5" />
        </motion.button>
      )}
    </AnimatePresence>
  );
};

export default BackToTopButton;
